import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { StatusPill } from "@ozpanel/kit";
import { Button, ConfirmDialog, Section, StateView, useAuth, useToast } from "@ozpanel/ui";

// Three small operations panels, each closing the last unreachable route in its module.

// ── redis: one credential per consumer, and changing it cluster-wide ───────

interface AclUser {
  service?: string;
  redis_user?: string;
  key_scope?: string[] | null;
  key_scope_note?: string;
  command_scope?: string[] | null;
  config_files?: string[] | null;
  secret_file?: string;
}

interface DefaultUserView {
  off?: boolean;
  measured?: boolean;
  note?: string;
}

interface SentinelAccountsView {
  port?: string;
  admin_held?: boolean;
  peers_authenticate_as?: string;
  default_commands?: string;
  default_discovery_only?: boolean;
  error?: string;
}

interface AclList {
  users: AclUser[] | null;
  total?: number;
  note?: string;
  default_user?: DefaultUserView;
  sentinels?: SentinelAccountsView[] | null;
  sentinel_note?: string;
}

// newSecretValue is a 32-character value from the browser's CSPRNG — letters and digits only, which every
// consumer's config format accepts (the cluster secret change refuses spaces and quotes).
// secretName is the cluster secret's name: /redis/acl reports the FILE (secret_file is its path), and the
// cluster change takes the name.
function secretName(u: AclUser | null): string {
  return (u?.secret_file ?? "").split("/").pop() ?? "";
}

function newSecretValue(): string {
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789";
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => alphabet[b % alphabet.length]).join("");
}

/**
 * The Redis actions an operator actually reaches for during an incident, and none of them were
 * reachable at all.
 *
 * /redis/failover, /redis/promote and /redis/sentinel/failover have existed and been declared
 * DANGER: HIGH since they were written, and the only way to run one was a shell on the node —
 * at the exact moment nobody wants to be composing redis-cli by hand. CLAUDE.md's Redis section
 * is almost entirely about failover behaviour that was drilled in both directions; this is that
 * drill made repeatable.
 *
 * All three are confirmed by name, because the difference between them is not obvious and the
 * wrong one during an incident makes it worse:
 *
 *   SENTINEL FAILOVER  asks the quorum to elect a new master. The safe one — it is the mechanism
 *                      that would have run by itself, driven deliberately.
 *   PROMOTE            REPLICAOF NO ONE on THIS node, with no election. It makes a second master
 *                      if the first is still alive, which is a split brain, and Sentinel is not
 *                      consulted. Only correct when the old master is provably gone.
 *
 * So promote states the split-brain consequence rather than warning generically, and the failover
 * button is offered first.
 */
export function RedisFailoverOps() {
  const { client } = useAuth();
  const qc = useQueryClient();
  const { notify } = useToast();
  const [confirm, setConfirm] = React.useState<{ title: string; body: React.ReactNode; label: string; run: () => Promise<unknown> } | null>(null);

  const act = useMutation({
    mutationFn: (fn: () => Promise<unknown>) => fn(),
    onSuccess: () => {
      notify({ kind: "success", title: "Done — check the topology above" });
      void qc.invalidateQueries({ queryKey: ["redis-topology"] });
    },
    onError: (e: unknown) => notify({ kind: "error", title: "It did not run", body: e instanceof Error ? e.message : String(e) }),
  });

  return (
    <Section title="Failover">
      <p className="mb-3 max-w-3xl text-[12px] leading-relaxed text-muted">
        Ask the Sentinel quorum to move the master, or force this node to become one. They are not
        the same operation and the second can split the cluster — each says what it does before
        you confirm it.
      </p>
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          disabled={act.isPending}
          onClick={() =>
            setConfirm({
              title: "Ask Sentinel to fail the master over?",
              label: "Fail over",
              body: (
                <div className="space-y-2 text-[12.5px]">
                  <p>
                    The Sentinel quorum elects a new master and every client follows it. This is
                    the mechanism that would run by itself if the master died — running it
                    deliberately is how a planned move or a drill is done.
                  </p>
                  <p className="text-muted">
                    Connections to the old master are dropped as it becomes a replica. Anything
                    mid-write retries against the new one.
                  </p>
                </div>
              ),
              run: () => client.apiFetch("/redis/sentinel/failover", { method: "POST", body: JSON.stringify({}) }),
            })
          }
        >
          Sentinel failover
        </Button>
        <Button
          size="sm"
          variant="danger"
          disabled={act.isPending}
          onClick={() =>
            setConfirm({
              title: "Force THIS node to become master?",
              label: "Promote it",
              body: (
                <div className="space-y-2 text-[12.5px]">
                  <p>
                    This runs REPLICAOF NO ONE here. There is no election and Sentinel is not
                    consulted.
                  </p>
                  <p className="text-crit">
                    If the current master is still running you now have TWO masters accepting
                    writes, and the writes that land on the losing one are gone when they
                    reconcile. Only do this when the old master is provably gone.
                  </p>
                </div>
              ),
              run: () => client.apiFetch("/redis/promote", { method: "POST", body: JSON.stringify({}) }),
            })
          }
        >
          Promote this node
        </Button>
      </div>

      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(o) => !o && setConfirm(null)}
        title={confirm?.title ?? ""}
        confirmLabel={confirm?.label ?? "Run"}
        destructive
        body={confirm?.body}
        onConfirm={() => {
          const c = confirm;
          setConfirm(null);
          if (c) act.mutate(c.run);
        }}
      />
    </Section>
  );
}

export function RedisAclOps() {
  const { client } = useAuth();
  const qc = useQueryClient();
  const { notify } = useToast();
  const [changing, setChanging] = React.useState<AclUser | null>(null);

  const q = useQuery({
    queryKey: ["redis-acl"],
    queryFn: () => client.apiFetch<AclList>("/redis/acl"),
    retry: false,
  });
  const du = q.data?.default_user;

  return (
    <Section title="Who can reach Redis" hint={q.data?.total != null ? `${q.data.total}` : undefined}>
      <StateView loading={q.isLoading} error={q.error} errorContext="the Redis credentials">
        {q.data?.note && <p className="mb-3 max-w-3xl text-[12px] leading-relaxed text-muted">{q.data.note}</p>}
        {du && (
          // Measured from Redis, not from what the panel wrote: whether a password alone still gets in.
          <div className="mb-3 flex flex-wrap items-start gap-2 border-b border-line pb-2">
            <StatusPill tone={du.measured ? (du.off ? "ok" : "bad") : "warn"}>
              {du.measured ? (du.off ? "default off" : "default ON") : "default unmeasured"}
            </StatusPill>
            <span className="max-w-3xl text-[11.5px] leading-relaxed text-muted">{du.note}</span>
          </div>
        )}
        <div className="space-y-2">
          {(q.data?.users ?? []).map((u) => (
            <div key={String(u.service)} className="flex flex-wrap items-start justify-between gap-3 border-b border-line py-2">
              <div className="min-w-0">
                <div className="text-[13px]">
                  {u.service}
                  <span className="ml-2 font-mono text-[11.5px] text-muted">{u.redis_user}</span>
                </div>
                {/* The scope is the interlock: a credential that can read every key is not
                    "scoped to its own data" however it is labelled, and the server explains per
                    consumer why its scope is what it is. */}
                {u.key_scope?.length ? (
                  <div className="mt-0.5 font-mono text-[11px] text-muted">keys {u.key_scope.join(" ")}</div>
                ) : null}
                {u.command_scope?.length ? (
                  <div className="mt-0.5 font-mono text-[11px] text-muted">commands {u.command_scope.join(" ")}</div>
                ) : null}
                {u.key_scope_note && (
                  <div className="mt-0.5 max-w-2xl text-[11px] leading-relaxed text-muted">{u.key_scope_note}</div>
                )}
              </div>
              {u.secret_file && (
                <Button size="sm" variant="ghost" onClick={() => setChanging(u)}>Change password</Button>
              )}
            </div>
          ))}
          {(q.data?.users ?? []).length === 0 && <div className="text-[13px] text-muted">No credentials configured.</div>}
        </div>

        {(q.data?.sentinels ?? []).length > 0 && (
          <div className="mt-4">
            <div className="mb-1 text-[12.5px] font-medium">Sentinel</div>
            {q.data?.sentinel_note && (
              <p className="mb-2 max-w-3xl text-[11.5px] leading-relaxed text-muted">{q.data.sentinel_note}</p>
            )}
            {(q.data?.sentinels ?? []).map((v) => (
              <div key={String(v.port)} className="flex flex-wrap items-center gap-2 border-b border-line py-1.5 text-[12px]">
                <span className="font-mono">:{v.port}</span>
                <StatusPill tone={v.default_discovery_only ? "ok" : "warn"}>
                  {v.default_discovery_only ? "default: discovery only" : "default: every right"}
                </StatusPill>
                <StatusPill tone={v.admin_held ? "ok" : "warn"}>{v.admin_held ? "ozsentinel held" : "ozsentinel missing"}</StatusPill>
                <span className="text-muted">peers authenticate as <span className="font-mono">{v.peers_authenticate_as || "?"}</span></span>
                {v.error && <span className="text-crit">{v.error}</span>}
              </div>
            ))}
          </div>
        )}
      </StateView>

      <ConfirmDialog
        open={!!changing}
        onOpenChange={(o) => !o && setChanging(null)}
        title={`Change ${changing?.service ?? ""}'s Redis password?`}
        confirmLabel="Change it on every node"
        destructive
        body={
          <div className="space-y-2 text-[12.5px]">
            <p>
              A new password is generated and changed <b>cluster-wide</b>: every node first accepts it
              beside the old one, then each node switches {changing?.service} over to it, and the old
              one is removed only after the last node has moved — so no node is ever refused.
            </p>
            {(changing?.config_files?.length ?? 0) > 0 && (
              <p className="text-muted">
                Rewritten on every node: <span className="font-mono">{(changing?.config_files ?? []).join(", ")}</span>.
              </p>
            )}
            <p className="text-muted">
              Progress per node: <span className="font-mono">GET /cluster/secrets/{secretName(changing)}/rollout</span>.
            </p>
          </div>
        }
        onConfirm={() => {
          const u = changing;
          setChanging(null);
          const name = secretName(u);
          if (!u || !name) return;
          void client
            .apiFetch(`/cluster/secrets/${encodeURIComponent(name)}`, {
              method: "PUT",
              body: JSON.stringify({ value: newSecretValue() }),
            })
            .then(() => {
              notify({ kind: "success", title: `${u.service}: new password recorded`, body: "Every node moves over within a few minutes." });
              void qc.invalidateQueries({ queryKey: ["redis-acl"] });
            })
            .catch((e: unknown) => notify({ kind: "error", title: "Could not change it", body: e instanceof Error ? e.message : String(e) }));
        }}
      />
    </Section>
  );
}

// ── redis: the accounts on the cluster's Redis, and the capacity they share ──

interface RedisTenantRow {
  tenant_id: number;
  tenant: string;
  enabled: boolean;
  why_not?: string;
  cap_mb: number;
  users: number;
  users_max: number;
  connections_max: number;
  used_bytes: number | null;
  keys: number;
  measured_at?: string;
  writes_paused: boolean;
}
interface RedisTenantsOverview {
  capacity?: { maxmemory: number; used: number; tenant_used: number; repl_backlog: number; headroom: number; platform_reserve: number };
  capacity_error?: string;
  available_to_accounts?: number;
  promised_bytes?: number;
  used_bytes?: number;
  accounts: RedisTenantRow[] | null;
  note?: string;
}

const mib = (b?: number | null) => (b == null ? "—" : `${(b / 1048576).toFixed(b < 10485760 ? 1 : 0)} MB`);

export function RedisTenantsOps() {
  const { client } = useAuth();
  const q = useQuery({
    queryKey: ["redis-tenants"],
    queryFn: () => client.apiFetch<RedisTenantsOverview>("/redis/tenants"),
    retry: false,
    refetchInterval: 30000,
  });
  const d = q.data;
  const c = d?.capacity;
  const rows = d?.accounts ?? [];
  return (
    <Section title="Accounts on Redis" hint={d ? `${rows.length}` : undefined}>
      <StateView loading={q.isLoading} error={q.error} errorContext="the accounts on Redis">
        {d?.note && <p className="mb-3 max-w-3xl text-[12px] leading-relaxed text-muted">{d.note}</p>}
        {c ? (
          // The invariant, measured: what the platform needs plus what plans promise must fit the cap.
          <div className="mb-3 grid gap-2 text-[12.5px] sm:grid-cols-4">
            <div><div className="text-[11px] uppercase tracking-wide text-muted">Redis limit</div><div className="font-mono">{mib(c.maxmemory)}</div></div>
            <div>
              <div className="text-[11px] uppercase tracking-wide text-muted">Platform reserve</div>
              <div className="font-mono">{mib(c.platform_reserve)}</div>
              <div className="text-[11px] text-muted">in use {mib(c.used - c.tenant_used)} + backlog {mib(c.repl_backlog)} + headroom {mib(c.headroom)}</div>
            </div>
            <div><div className="text-[11px] uppercase tracking-wide text-muted">Promised to accounts</div><div className="font-mono">{mib(d?.promised_bytes)} of {mib(d?.available_to_accounts)}</div></div>
            <div><div className="text-[11px] uppercase tracking-wide text-muted">Used by accounts</div><div className="font-mono">{mib(d?.used_bytes)}</div></div>
          </div>
        ) : (
          <div className="mb-3 text-[12px] text-warn">Capacity not measured — {d?.capacity_error ?? "unknown"}</div>
        )}
        {rows.length === 0 ? (
          <div className="text-[13px] text-muted">No account has Redis. Give a plan (or one account) a Redis memory allowance to offer it.</div>
        ) : (
          <div className="space-y-1">
            {rows.map((r) => (
              <div key={r.tenant_id} className="flex flex-wrap items-center gap-2 border-b border-line py-1.5 text-[12.5px]">
                <span className="min-w-[10rem]">{r.tenant}</span>
                {!r.enabled ? (
                  <StatusPill tone="neutral">{r.why_not || "off"}</StatusPill>
                ) : r.writes_paused ? (
                  <StatusPill tone="bad">writes paused at cap</StatusPill>
                ) : (
                  <StatusPill tone="ok">on</StatusPill>
                )}
                <span className="font-mono">{r.used_bytes == null ? "not measured" : mib(r.used_bytes)} of {r.cap_mb} MB</span>
                <span className="text-muted">{r.keys} keys · {r.users}{r.users_max > 0 ? ` of ${r.users_max}` : ""} users · ≤{r.connections_max} connections</span>
              </div>
            ))}
          </div>
        )}
      </StateView>
    </Section>
  );
}

// ── php-fpm: the pools, and removing one ──────────────────────────────────

export function PhpPoolOps() {
  const { client } = useAuth();
  const qc = useQueryClient();
  const { notify } = useToast();
  const [removing, setRemoving] = React.useState<string | null>(null);

  const q = useQuery({
    queryKey: ["phpfpm-pools"],
    queryFn: () => client.apiFetch<{ pools: string[] | null; total?: number }>("/phpfpm/pools"),
    retry: false,
  });

  return (
    <Section title="PHP pools" hint={q.data?.total != null ? `${q.data.total}` : undefined}>
      <StateView loading={q.isLoading} error={q.error} errorContext="the PHP pools">
        <p className="mb-2 max-w-3xl text-[12px] leading-relaxed text-muted">
          One pool per site, each running as its own tenant user — which is what stops one
          customer&apos;s PHP reading another&apos;s files.
        </p>
        <div className="space-y-1">
          {(q.data?.pools ?? []).map((p) => (
            <div key={p} className="flex items-center justify-between gap-3 border-b border-line py-1.5">
              <span className="font-mono text-[12.5px]">{p}</span>
              <Button size="sm" variant="danger" onClick={() => setRemoving(p)}>Remove</Button>
            </div>
          ))}
          {(q.data?.pools ?? []).length === 0 && <div className="text-[13px] text-muted">No pools configured.</div>}
        </div>
      </StateView>

      <ConfirmDialog
        open={!!removing}
        onOpenChange={(o) => !o && setRemoving(null)}
        title={`Remove the PHP pool for ${removing ?? ""}?`}
        confirmLabel="Remove it"
        destructive
        body={
          <p>
            Every PHP page on that site stops working immediately — the site itself stays
            configured, so visitors get an error from the web server rather than a missing site.
            Files are untouched.
          </p>
        }
        onConfirm={() => {
          const p = removing;
          setRemoving(null);
          if (!p) return;
          void client
            .apiFetch(`/phpfpm/pools/${encodeURIComponent(p)}`, { method: "DELETE" })
            .then(() => {
              notify({ kind: "success", title: "Pool removed" });
              void qc.invalidateQueries({ queryKey: ["phpfpm-pools"] });
            })
            .catch((e: unknown) => notify({ kind: "error", title: "Could not remove it", body: e instanceof Error ? e.message : String(e) }));
        }}
      />
    </Section>
  );
}

// ── the node's own health, as a terminal would print it ───────────────────

export function HealthTerminal() {
  const { client } = useAuth();
  const q = useQuery({
    queryKey: ["health-terminal"],
    queryFn: () => client.apiFetch<{ status?: string; checks?: { name?: string; ok?: boolean; detail?: string }[] | null }>("/ozpanel/health/terminal"),
    retry: false,
    refetchInterval: 60000,
  });

  return (
    <Section
      title="This node"
      actions={q.data?.status ? <StatusPill tone={q.data.status === "healthy" ? "ok" : "bad"}>{q.data.status}</StatusPill> : undefined}
    >
      <StateView loading={q.isLoading} error={q.error} errorContext="this node's health">
        <ul className="space-y-1">
          {(q.data?.checks ?? []).map((c, i) => (
            <li key={i} className="flex items-start gap-2 border-b border-line py-1 text-[12.5px]">
              <span className={c.ok ? "text-ok" : "text-crit"}>{c.ok ? "✓" : "✗"}</span>
              <span>
                {(c.name ?? "check").replace(/_/g, " ")}
                {c.detail && <span className="ml-2 text-[11.5px] text-muted">{c.detail}</span>}
              </span>
            </li>
          ))}
          {(q.data?.checks ?? []).length === 0 && <li className="text-[13px] text-muted">Nothing reported.</li>}
        </ul>
      </StateView>
    </Section>
  );
}
